import React from "react";
import {
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherCopy,
  FeatherEye,
} from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Loader } from "../../../../ui/components/Loader";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { truncateText } from "../../../../shared/utils/formatters";
import { JournalEntity, PaginationInfo } from "../../domain";
import { JournalStatusChip } from "./JournalStatusChip";
import {
  ActiveLocation,
  useActiveLocations,
} from "../../../../shared/hooks/useActiveLocations";

interface JournalTableProps {
  journals: JournalEntity[];
  loading: boolean;
  inlineLoading?: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  itemsPerPage: number;
  searchTerm: string;
  canDuplicateJournal?: boolean;
  duplicatingJournalId?: number | null;
  onView: (journal: JournalEntity) => void;
  onDuplicate?: (journal: JournalEntity) => void;
  onPageChange: (page: number) => void;
}

export const JournalTable: React.FC<JournalTableProps> = ({
  journals,
  loading,
  inlineLoading = false,
  error,
  pagination,
  itemsPerPage,
  searchTerm,
  canDuplicateJournal = false,
  duplicatingJournalId = null,
  onView,
  onDuplicate,
  onPageChange,
}) => {
  // ZOUR-65 F4: the Location column exists only for a company with more than
  // one GST registration. On every other tenant the list is unchanged — the
  // column is not rendered and the colSpans stay at 7.
  const { locations, hasMultiple: hasMultipleLocations } = useActiveLocations();
  const columnCount = hasMultipleLocations ? 8 : 7;

  return (
    <div className="flex w-full flex-col items-start gap-2">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Journal Entries
        </span>
      </div>
      <Table
        header={
          <Table.HeaderRow>
            <Table.HeaderCell>Entry ID</Table.HeaderCell>
            <Table.HeaderCell>Recurrence</Table.HeaderCell>
            <Table.HeaderCell>Debit</Table.HeaderCell>
            <Table.HeaderCell>Credit</Table.HeaderCell>
            <Table.HeaderCell className="h-8 w-80 flex-none">
              Notes
            </Table.HeaderCell>
            {hasMultipleLocations && (
              <Table.HeaderCell>Location</Table.HeaderCell>
            )}
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.HeaderRow>
        }
      >
        {loading ? (
          <TableSkeletonRows rowCount={itemsPerPage} columnCount={columnCount} />
        ) : inlineLoading ? (
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <div className="flex w-full items-center justify-center py-8">
                <Loader />
              </div>
            </Table.Cell>
          </Table.Row>
        ) : error ? (
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-error-600">
                  {error}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : journals.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-neutral-500">
                  {searchTerm.trim()
                    ? "No journals match your search"
                    : "No journals found"}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : (
          journals.map((journal) => (
            <JournalTableRow
              key={`${journal.journal_type ?? "manual"}-${journal.id}`}
              journal={journal}
              locations={locations}
              showLocation={hasMultipleLocations}
              canDuplicateJournal={canDuplicateJournal}
              duplicatingJournalId={duplicatingJournalId}
              onView={onView}
              onDuplicate={onDuplicate}
            />
          ))
        )}
      </Table>

      {!loading && !inlineLoading && pagination && (
        <JournalPagination
          pagination={pagination}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

interface JournalTableRowProps {
  journal: JournalEntity;
  locations: ActiveLocation[];
  showLocation: boolean;
  canDuplicateJournal: boolean;
  duplicatingJournalId: number | null;
  onView: (journal: JournalEntity) => void;
  onDuplicate?: (journal: JournalEntity) => void;
}

const JournalTableRow: React.FC<JournalTableRowProps> = ({
  journal,
  locations,
  showLocation,
  canDuplicateJournal,
  duplicatingJournalId,
  onView,
  onDuplicate,
}) => {
  const isManualApproved =
    journal.journal_type !== "ap_ar" &&
    journal.status === "approved" &&
    (!!journal.zoho_journalid ||
      !!journal.tally_remote_id ||
      !!journal.tally_master_id ||
      !!journal.posted_to_tally_date);

  return (
    <Table.Row>
      <Table.Cell className="align-top py-4">
        <div className="flex flex-col items-start gap-1">
          <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
            {journal.journal_number || journal.id}
          </span>
          <span
            className={`whitespace-nowrap text-caption font-caption ${
              journal.date === "1900-01-01"
                ? "text-warning-600 italic"
                : "text-subtext-color"
            }`}
          >
            {formatJournalDate(journal.date)}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell className="align-top py-4">
        <RecurrenceCell journal={journal} />
      </Table.Cell>
      <Table.Cell className="align-top py-4">
        <span className="whitespace-nowrap text-body font-body text-neutral-500">
          {formatJournalAmount(journal.currency_symbol, journal.total_debit)}
        </span>
      </Table.Cell>
      <Table.Cell className="align-top py-4">
        <span className="whitespace-nowrap text-body font-body text-neutral-500">
          {formatJournalAmount(journal.currency_symbol, journal.total_credit)}
        </span>
      </Table.Cell>
      <Table.Cell className="h-12 w-80 flex-none align-top py-4">
        <span className="line-clamp-2 text-body font-body text-neutral-500">
          {truncateText(journal.notes || "", 120)}
        </span>
      </Table.Cell>
      {showLocation && (
        <Table.Cell className="align-top py-4">
          <JournalLocationCell journal={journal} locations={locations} />
        </Table.Cell>
      )}
      <Table.Cell className="align-top py-4">
        <div className="flex flex-col items-start gap-1">
          <JournalStatusChip journal={journal} />
          {journal.updated_at && (
            <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
              Modified {formatJournalDate(journal.updated_at)}
            </span>
          )}
        </div>
      </Table.Cell>
      <Table.Cell className="align-top py-4">
        <div className="flex items-center gap-2">
          <IconButton icon={<FeatherEye />} onClick={() => onView(journal)} />
          {isManualApproved && onDuplicate && (
            <IconButton
              icon={
                duplicatingJournalId === journal.id ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                ) : (
                  <FeatherCopy />
                )
              }
              onClick={() => onDuplicate(journal)}
              disabled={!canDuplicateJournal || duplicatingJournalId === journal.id}
            />
          )}
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

/**
 * ZOUR-65 F4 — which GST registration this journal is filed under.
 *
 * A NULL `location_id` is the DEFAULT answer, not a missing one: it means the
 * primary location, which is where Zoho files an unlocated document. So the
 * primary's name is shown for it rather than a dash, and the reader is never
 * left wondering whether the row is unfiled.
 *
 * An AP/AR journal is the exception. It has no location of its own — it derives
 * from the bill or invoice it was raised for — so claiming a registration for it
 * here would be an invention.
 *
 * Name in the cell, GSTIN in the title: "Bengaluru Branch" means nothing when
 * you are checking a registration, but the column has to stay narrow.
 */
const JournalLocationCell: React.FC<{
  journal: JournalEntity;
  locations: ActiveLocation[];
}> = ({ journal, locations }) => {
  if (journal.journal_type === "ap_ar") {
    return (
      <span className="whitespace-nowrap text-body font-body text-neutral-400">
        —
      </span>
    );
  }

  const primary = locations.find((location) => location.is_primary) ?? null;
  const location =
    locations.find(
      (candidate) => candidate.zoho_location_id === journal.location_id,
    ) ??
    (journal.location_id ? null : primary);

  if (!location) {
    return (
      <span className="whitespace-nowrap text-body font-body text-neutral-400">
        —
      </span>
    );
  }

  return (
    <span
      className="whitespace-nowrap text-body font-body text-neutral-500"
      title={
        location.gstin
          ? `${location.gstin}${location.state_code ? ` (${location.state_code})` : ""}`
          : undefined
      }
    >
      {location.name}
    </span>
  );
};

const RecurrenceCell: React.FC<{ journal: JournalEntity }> = ({ journal }) => {
  if (journal.template_id || journal.is_generated_from_template) {
    return <Badge variant="warning">{getRecurrenceLabel(journal)}</Badge>;
  }

  return (
    <span className="whitespace-nowrap text-body font-body text-neutral-500">
      One-time
    </span>
  );
};

interface JournalPaginationProps {
  pagination: PaginationInfo;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const JournalPagination: React.FC<JournalPaginationProps> = ({
  pagination,
  itemsPerPage,
  onPageChange,
}) => {
  const currentPage =
    pagination.current_page ?? Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages =
    pagination.total_pages ?? Math.ceil(pagination.total / pagination.limit);

  if (totalPages <= 1) return null;

  const start = pagination.offset + 1;
  const end = Math.min(pagination.offset + itemsPerPage, pagination.total);
  const pages = getVisiblePages(currentPage, totalPages);

  return (
    <div className="flex w-full items-center justify-between mt-4">
      <span className="text-body font-body text-subtext-color">
        Showing {start} to {end} of {pagination.total} results
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          icon={<FeatherChevronLeft />}
        >
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {pages.map((page) => (
            <Button
              key={page}
              variant={page === currentPage ? "brand-primary" : "neutral-tertiary"}
              size="small"
              onClick={() => onPageChange(page)}
              className="min-w-[32px]"
            >
              {page}
            </Button>
          ))}
        </div>
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!pagination.has_more}
          icon={<FeatherChevronRight />}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const getVisiblePages = (currentPage: number, totalPages: number): number[] => {
  const visibleCount = Math.min(5, totalPages);
  if (totalPages <= visibleCount) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return Array.from({ length: visibleCount }, (_, index) => index + 1);
  }

  if (currentPage >= totalPages - 2) {
    return Array.from(
      { length: visibleCount },
      (_, index) => totalPages - visibleCount + index + 1,
    );
  }

  return Array.from(
    { length: visibleCount },
    (_, index) => currentPage - 2 + index,
  );
};

const formatJournalDate = (dateString: string): string => {
  if (!dateString) return "N/A";
  if (dateString === "1900-01-01") return "Date not selected";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatJournalAmount = (currencySymbol: string, amount: number): string =>
  `${currencySymbol || ""}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getRecurrenceLabel = (journal: JournalEntity): string => {
  switch (journal.recurrence_frequency) {
    case "start_of_month":
    case "end_of_month":
      return "Recurring • Monthly";
    case "daily":
      return "Recurring • Daily";
    case "every_n_days":
      return journal.recurrence_interval
        ? `Recurring • Every ${journal.recurrence_interval} days`
        : "Recurring";
    default:
      return "Recurring";
  }
};
